import React, {useEffect, useCallback, useState} from 'react';
import * as Sentry from "@sentry/react";
import {initFlowbite} from 'flowbite';
import {Participant, ParticipantSettings} from './types';
import {useAuth} from './libraries/AuthContext';
import {
    useAppState,
    useAppDispatch,
    loadEventData as loadEventDataAction,
    navigateTo,
    navigateHome,
    navigateToParticipant,
    navigateToEditParticipant,
    navigateToArbeitsgruppe,
    navigateToChat,
    updateParticipant,
    addConversation,
    castAgVote,
    updateMeSettings,
    unblockParticipant,
} from './libraries/AppContext';
import Header from './components/Header';
import HomePage from './components/HomePage';
import AgendaPage from './components/AgendaPage';
import GenericPage from './components/GenericPage';
import ParticipantsPage from './components/ParticipantsPage';
import ParticipantDetailPage from './components/ParticipantDetailPage';
import EditParticipantPage from './components/EditParticipantPage';
import LoadingSpinner from './components/LoadingSpinner';
import Alert from './components/ErrorDisplay';
import OnboardingLoginPage from './components/OnboardingLoginPage';
import NavigationDrawer from './components/NavigationDrawer';
import ArbeitsgruppenPage from './components/ArbeitsgruppenPage';
import ArbeitsgruppeDetailPage from './components/ArbeitsgruppeDetailPage';
import ActivityGroupsPage from './components/ActivityGroupsPage';
import ChatPage from './components/ChatPage';
import ChatWindow from './components/ChatWindow';
import AGVotingPage from './components/AGVotingPage';
import SettingsPage from './components/SettingsPage';
import GalleryPage from './components/GalleryPage';
import ChallengeResultsPage from './components/ChallengeResultsPage';
import {authenticatedFetch, getFirebaseMessagingToken} from './libraries/auth';
import {initNotifications, removeNotificationListeners, syncFirebaseMessagingToken} from "@/libraries/notification.ts";


const API_URL = import.meta.env.VITE_API_URL || '';


const App: React.FC = () => {
    const {isAuthenticated, isLoading: authLoading, login, logout} = useAuth();
    const {eventData, loading, error, view, online, lastUpdated} = useAppState();
    const dispatch = useAppDispatch();
    const [isCreateChatOpen, setIsCreateChatOpen] = useState(false);
    const [startingChatParticipantId, setStartingChatParticipantId] = useState<number | null>(null);
    const [chatStartError, setChatStartError] = useState<string | null>(null);

    useEffect(() => {
        if (__IS_CORDOVA__) {
            if (eventData && eventData.me && eventData.me.Participant.settings.pushNotifications) {
                initNotifications()
            }
        }
        return () => {
            if (__IS_CORDOVA__) {
                removeNotificationListeners();
            }
        }
    }, [isAuthenticated, eventData]);

    useEffect(() => {
        // Initialize Flowbite components after DOM updates
        const timer = setTimeout(() => {
            initFlowbite();
        }, 100);
        return () => clearTimeout(timer);
    }, [view, eventData]);

    const processHotelReservationContent = useCallback((content: string) => {
        if (!content) return '';
        if (!eventData || !eventData.me) return content;
        if (!eventData.me.Participant.hotelReservation) {
            return content.replace('<hotel-reservation-info></hotel-reservation-info>', '');
        }
        return content.replace('<hotel-reservation-info></hotel-reservation-info>',
            `
<p><strong>Hotel</strong><br/>
Wenn Wir haben für Sie im BOLD Campus ein Zimmer ab Sonntag, den 22.02.2026, reserviert.<br/>
${eventData.me.Participant.hotelReservation}</p>`);
    }, [eventData]);

    const processShuttleInfoContent = useCallback((content: string) => {
        if (!content) return '';
        if (!eventData || !eventData.me) return content;
        if (eventData.me.Participant.shuttle !== 'ja') {
            return content.replace('<shuttle-info></shuttle-info>', '');
        }
        // Replace {{SHUTTLE_INFO}} with actual shuttle information if available
        return content
            .replace('<shuttle-info></shuttle-info>', `
<p><strong>Shuttle</strong><br/>
Wir haben Ihre Anreise am ${eventData.me.Participant.ankunftBahnhof || 'Bahnhof Eppstein oder Fernbahnhof / Frankfurt Flughafen'}
 um ${eventData.me.Participant.ankunftZeit.trim() + " Uhr" || "k. A."} notiert.<br/>
Alle weiteren Informationen zum Shuttle finden Sie im gleichnamigen Button.
</p>
`)
    }, [eventData]);

    const processPageContent = useCallback((content: string) => {
        content = processHotelReservationContent(content);
        content = processShuttleInfoContent(content);
        return content;
    }, [processHotelReservationContent, processShuttleInfoContent]);

    const reloadEventData = useCallback(() => {
        loadEventDataAction(dispatch, logout);
    }, [dispatch, logout]);

    useEffect(() => {
        if (isAuthenticated && !authLoading) {
            reloadEventData();
        }
    }, [isAuthenticated, authLoading, reloadEventData]);

    useEffect(() => {
        if (view.name !== 'chat' || view.conversationId) {
            setIsCreateChatOpen(false);
        }
    }, [view]);

    useEffect(() => {
        window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
    }, [view]);

    // Utility to close the Flowbite drawer if open
    const closeDrawer = () => {
        const closeButton = document.querySelector('[data-drawer-hide="navigation-drawer"]') as HTMLElement;
        if (closeButton) {
            closeButton.click();
        }
    };

    const handleNavigate = useCallback((sectionType: string, pageId: number | null, url: string | null) => {
        closeDrawer();
        if (sectionType === 'agenda') {
            navigateTo(dispatch, {name: 'agenda'});
        } else if (sectionType === 'participants') {
            navigateTo(dispatch, {name: 'participants'});
        } else if (sectionType === 'profile') {
            navigateTo(dispatch, {name: 'profile'});
        } else if (sectionType === 'arbeitsgruppen') {
            navigateTo(dispatch, {name: 'arbeitsgruppen'});
        } else if (sectionType === 'activity_groups') {
            navigateTo(dispatch, {name: 'activity_groups'});
        } else if (sectionType === 'voting') {
            navigateTo(dispatch, {name: 'voting'});
        } else if (sectionType === 'settings') {
            navigateTo(dispatch, {name: 'settings'});
        } else if (sectionType === 'page' && pageId) {
            navigateTo(dispatch, {name: 'page', id: pageId});
        } else if (sectionType === 'url' && url) {
            window.open(url, '_blank', 'noopener,noreferrer');
        } else if (sectionType === 'logout') {
            logout();
        } else if (sectionType === 'chat' || sectionType === 'chat_conversations') {
            navigateTo(dispatch, {name: 'chat'}); // List view
        } else if (sectionType === 'gallery') {
            navigateTo(dispatch, {name: 'gallery'});
        } else if (sectionType === 'challenge_results') {
            navigateTo(dispatch, {name: 'challenge_results'});
        }
    }, [dispatch, logout]);

    const handleBack = useCallback(() => {
        closeDrawer();
        if (view.name === 'participant') {
            navigateTo(dispatch, {name: 'participants'});
        } else if (view.name === 'arbeitsgruppe') {
            navigateTo(dispatch, {name: 'arbeitsgruppen'});
        } else if (view.name === 'voting') {
            navigateHome(dispatch);
        } else if (view.name === 'challenge_results') {
            navigateHome(dispatch);
        } else if (view.name === 'editParticipant') {
            // Check if we came from profile or participant view
            const participant = eventData?.participants.find(p => p.id === view.id);
            if (participant && eventData && participant.userId === eventData.me.id) {
                navigateTo(dispatch, {name: 'profile'});
            } else {
                navigateToParticipant(dispatch, view.id);
            }
        } else if (view.name === 'profile') {
            navigateHome(dispatch);
        } else if (view.name === 'chat' && view.conversationId) {
            if (view.fromParticipantId) {
                navigateToParticipant(dispatch, view.fromParticipantId);
            } else {
                navigateTo(dispatch, {name: 'chat'}); // Go back to list
            }
        } else if (view.name === 'chat') {
            navigateHome(dispatch);
        } else {
            navigateHome(dispatch);
        }
    }, [dispatch, view, eventData]);

    const handleParticipantClick = useCallback((participantId: number) => {
        navigateToParticipant(dispatch, participantId);
    }, [dispatch]);

    const handleArbeitsgruppeClick = useCallback((id: number) => {
        if (id === 0) {
            // Navigate to arbeitsgruppen list page
            navigateTo(dispatch, {name: 'arbeitsgruppen'});
        } else {
            // Navigate to arbeitsgruppe detail page
            navigateToArbeitsgruppe(dispatch, id);
        }
    }, [dispatch]);

    const handleActivityGroupClick = useCallback((id: number) => {
        if (id === 0) {
            // Navigate to activity_groups list page
            navigateTo(dispatch, {name: 'activity_groups'});
        } else {
            // Navigate to activity_groups list page (detail page removed)
            navigateTo(dispatch, {name: 'activity_groups'});
        }
    }, [dispatch]);

    const handleEditParticipant = useCallback((participantId: number) => {
        navigateToEditParticipant(dispatch, participantId);
    }, [dispatch]);

    const handleSaveParticipant = useCallback((updatedParticipant: Participant) => {
        // Update participant in the local state
        updateParticipant(dispatch, updatedParticipant);
        // Navigate back - check if it's the current user's profile
        if (eventData && updatedParticipant.userId === eventData.me.id) {
            navigateTo(dispatch, {name: 'profile'});
        } else {
            navigateToParticipant(dispatch, updatedParticipant.id);
        }
    }, [dispatch, eventData]);

    const handleCancelEdit = useCallback(() => {
        if (view.name === 'editParticipant') {
            const participant = eventData?.participants.find(p => p.id === view.id);
            if (participant && eventData && participant.userId === eventData.me.id) {
                navigateTo(dispatch, {name: 'profile'});
            } else {
                navigateToParticipant(dispatch, view.id);
            }
        }
    }, [dispatch, view, eventData]);

    const handleLoginSuccess = async (user: any, token: string) => {
        login(user, token);
        if (__IS_CORDOVA__) {
            const firebaseToken = getFirebaseMessagingToken()
            if (firebaseToken) {
                await syncFirebaseMessagingToken(firebaseToken);
            }
        }
    };

    const handleChatStart = useCallback(async (participantId: number): Promise<boolean> => {
        if (startingChatParticipantId !== null) {
            return false;
        }

        setChatStartError(null);
        setStartingChatParticipantId(participantId);

        try {
            // Call API to start/get conversation
            const response = await authenticatedFetch(`${API_URL}/api/chat/start`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({participantId})
            });

            if (!response.ok) {
                throw new Error('Failed to start chat');
            }

            const conversation = await response.json();

            // Check if conversation already exists in state, if not add it
            const existingConv = eventData?.chat_conversations?.find(c => c.id === conversation.id);
            if (!existingConv) {
                // Build the conversation object with participant details
                const participant = eventData?.participants.find(p => p.id === participantId);
                if (participant) {
                    const newConversation = {
                        id: conversation.id,
                        updatedAt: conversation.updatedAt || new Date().toISOString(),
                        otherParticipant: {
                            id: participant.id,
                            vorname: participant.vorname,
                            nachname: participant.nachname,
                            profileImage: participant.profileImage,
                        }
                    };
                    addConversation(dispatch, newConversation);
                }
            }

            // Determine source view
            let fromParticipantId: number | undefined;
            if (view.name === 'participant') {
                fromParticipantId = view.id;
            }

            navigateToChat(dispatch, conversation.id, fromParticipantId);
            setIsCreateChatOpen(false);
            return true;
        } catch (err) {
            console.error(err);
            Sentry.captureException(err);
            const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
            setChatStartError(
                isOffline
                    ? 'Keine Internetverbindung. Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.'
                    : 'Der Chat konnte gerade nicht gestartet werden. Bitte versuchen Sie es in ein paar Sekunden erneut.'
            );
            return false;
        } finally {
            setStartingChatParticipantId(null);
        }
    }, [dispatch, view, eventData, startingChatParticipantId]);

    const handleCastAgVote = useCallback(async (questionId: number, arbeitsgruppeId: number) => {
        const response = await authenticatedFetch(`${API_URL}/api/voting/cast`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({questionId, arbeitsgruppeId}),
        });

        const data = await response.json().catch(() => null);
        if (!response.ok) {
            throw new Error(data?.error || 'Stimme konnte nicht gespeichert werden');
        }

        castAgVote(dispatch, questionId, arbeitsgruppeId);
    }, [dispatch]);

    const handleSaveSettings = useCallback((settings: ParticipantSettings) => {
        updateMeSettings(dispatch, settings);
    }, [dispatch]);

    const handleParticipantUnblocked = useCallback((participantId: number, userId: number) => {
        unblockParticipant(dispatch, participantId, userId);
    }, [dispatch]);

    const renderContent = () => {
        if (!eventData) return null;

        // Calculate myParticipantId
        const myUser = eventData.users.find(u => u.id === eventData.me.id);
        const myParticipantId = myUser?.participantId ?? null;

        const myArbeitsgruppe = eventData.arbeitsgruppen.find(ag =>
            eventData.me.arbeitsgruppenIds.includes(ag.id)
        );
        const myArbeitsgruppeId = myArbeitsgruppe ? myArbeitsgruppe.id : null;
        const myArbeitsgruppeName = myArbeitsgruppe ? `Arbeitsgruppe: ${myArbeitsgruppe.name}` : '';

        switch (view.name) {
            case 'home':
                return (
                    <HomePage
                        homepage={eventData.homepage}
                        chatConversations={eventData.chat_conversations}
                        onNavigate={handleNavigate}
                    />
                );
            case 'agenda':
                const agendaWithReplacedNames = eventData.agenda.map(item => {
                    // Replace placeholders and inject Arbeitsgruppe room when available
                    const replacedTitle = item.title.replace('{{ARBEITSGRUPPE_NAME}}', myArbeitsgruppeName);
                    const replacedDescription = item.description ? item.description.replace('{{ARBEITSGRUPPE_NAME}}', myArbeitsgruppeName) : item.content;
                    const resolvedPlace = item.type === 'arbeitsgruppen' && myArbeitsgruppe?.room ? myArbeitsgruppe.room : item.place;

                    return {
                        ...item,
                        title: replacedTitle,
                        description: replacedDescription,
                        place: resolvedPlace
                    };
                });
                return (
                    <AgendaPage
                        agendaItems={agendaWithReplacedNames}
                        activityGroups={eventData.activityGroups}
                        myArbeitsgruppeId={myArbeitsgruppeId}
                        myActivityGroupIds={eventData.me.activityGroupIds}
                        onArbeitsgruppeClick={handleArbeitsgruppeClick}
                        onActivityGroupClick={handleActivityGroupClick}
                    />
                );
            case 'participants':
                return <ParticipantsPage participants={eventData.participants}
                                         onParticipantClick={handleParticipantClick} currentUser={eventData.me}
                                         users={eventData.users}/>;
            case 'participant':
                const participant = eventData.participants.find(p => p.id === view.id);
                return participant ? <ParticipantDetailPage participant={participant} onEdit={handleEditParticipant}
                                                            currentUserId={eventData.me.id}
                                                            onProfileImageUpdate={reloadEventData}
                                                            onChat={handleChatStart}
                                                            isStartingChat={startingChatParticipantId === participant.id}/> :
                    <Alert title="Nicht gefunden" message="Der angeforderte Teilnehmer konnte nicht gefunden werden."
                           type="error"/>;
            case 'profile':
                const currentUserData = eventData.users.find(u => u.id === eventData.me.id);
                if (currentUserData?.participantId) {
                    const myParticipant = eventData.participants.find(p => p.id === currentUserData.participantId);
                    return myParticipant ?
                        <ParticipantDetailPage participant={myParticipant} onEdit={handleEditParticipant}
                                               currentUserId={eventData.me.id} onProfileImageUpdate={reloadEventData}
                                               onChat={handleChatStart}
                                               isStartingChat={startingChatParticipantId === myParticipant.id}/> :
                        <Alert title="Nicht gefunden" message="Du hast noch kein Teilnehmerprofil." type="info"/>;
                }
                return <Alert title="Nicht gefunden" message="Du hast noch kein Teilnehmerprofil." type="info"/>;
            case 'editParticipant':
                const participantToEdit = eventData.participants.find(p => p.id === view.id);
                return participantToEdit ?
                    <EditParticipantPage participant={participantToEdit} onSave={handleSaveParticipant}
                                         onCancel={handleCancelEdit}/> :
                    <Alert title="Nicht gefunden" message="Der angeforderte Teilnehmer konnte nicht gefunden werden."
                           type="error"/>;
            case 'page':
                const page = eventData.pages.find(p => p.id === view.id);
                page && (page.content = processPageContent(page.content));
                return page ? <GenericPage page={page}/> :
                    <Alert title="Nicht gefunden" message="Die angeforderte Seite konnte nicht gefunden werden."
                           type="error"/>;
            case 'arbeitsgruppen':
                return <ArbeitsgruppenPage arbeitsgruppen={eventData.arbeitsgruppen}
                                           onArbeitsgruppeClick={handleArbeitsgruppeClick}
                                           myArbeitsgruppenIds={eventData.me.arbeitsgruppenIds}/>;
            case 'arbeitsgruppe':
                const ag = eventData.arbeitsgruppen.find(g => g.id === view.id);
                return ag ? (
                    <ArbeitsgruppeDetailPage
                        arbeitsgruppe={ag}
                        myArbeitsgruppenIds={eventData.me.arbeitsgruppenIds}
                        myParticipantId={myParticipantId}
                        onNavigateToArbeitsgruppe={handleArbeitsgruppeClick}

                    />
                ) : <Alert title="Not found" message="Arbeitsgruppe not found" type="error"/>;
            case 'activity_groups':
                return (
                    <ActivityGroupsPage
                        activityGroups={eventData.activityGroups}
                        onActivityGroupClick={handleActivityGroupClick}
                        myActivityGroupIds={eventData.me.activityGroupIds}
                        myParticipantId={myParticipantId}
                    />
                );
            case 'voting':
                return (
                    <AGVotingPage
                        questions={eventData.agVotingQuestions}
                        arbeitsgruppen={eventData.arbeitsgruppen}
                        myArbeitsgruppenIds={eventData.me.arbeitsgruppenIds}
                        onCastVote={handleCastAgVote}
                    />
                );
            case 'settings':
                return (
                    <SettingsPage
                        settings={eventData.me.Participant?.settings}
                        onSettingsSaved={handleSaveSettings}
                        blockedParticipants={eventData.participants.filter(p => p.isBlocked)}
                        onParticipantUnblocked={handleParticipantUnblocked}
                    />
                );
            case 'chat':
                if (view.conversationId) {
                    return <ChatWindow conversationId={view.conversationId}/>;
                }
                return (
                    <ChatPage
                        onConversationClick={(id) => navigateToChat(dispatch, id)}
                        onStartChat={handleChatStart}
                        isCreateOpen={isCreateChatOpen}
                        onCloseCreate={() => setIsCreateChatOpen(false)}
                        startingChatParticipantId={startingChatParticipantId}
                    />
                );
            case 'gallery':
                return <GalleryPage media={eventData.gallery ?? []} />;
            case 'challenge_results':
                return <ChallengeResultsPage arbeitsgruppen={eventData.arbeitsgruppen} />;
            default:
                return null;
        }
    };

    const getHeaderTitle = () => {
        if (view.name === 'agenda') return 'Agenda';
        if (view.name === 'participants') return 'Teilnehmer';
        if (view.name === 'profile') return 'Mein Profil';
        if (view.name === 'arbeitsgruppen') return 'Hackathon';
        if (view.name === 'arbeitsgruppe') return 'Hackathon';
        if (view.name === 'voting') return 'Abstimmung';
        if (view.name === 'settings') return 'Einstellungen';
        if (view.name === 'activity_groups') return 'Aktivitäten';
        if (view.name === 'gallery') return 'Galerie';
        if (view.name === 'challenge_results') return 'Ergebnisse Challenges';
        if (view.name === 'participant' && eventData) {
            const participant = eventData.participants.find(p => p.id === view.id);
            return participant ? `${participant.vorname} ${participant.nachname}` : 'Teilnehmer';
        }
        if (view.name === 'editParticipant') return 'Profil bearbeiten';
        if (view.name === 'page' && eventData) {
            const page = eventData.pages.find(p => p.id === view.id);
            return page ? page.title : 'Seite';
        }
        if (view.name === 'chat') {
            return view.conversationId ? 'Chat' : 'Nachrichten';
        }
        return 'EventApp';
    };

    // Show loading spinner while checking auth status
    if (authLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <LoadingSpinner/>
            </div>
        );
    }

    // Show login page if not authenticated
    if (!isAuthenticated) {
        return <OnboardingLoginPage onLoginSuccess={handleLoginSuccess}/>;
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <LoadingSpinner/>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 sm:p-8">
                <Alert title="Fehler" message={`Laden der Veranstaltungsdaten fehlgeschlagen: ${error}`} type="error"
                       onRetry={reloadEventData}/>
            </div>
        );
    }

    if (!eventData) {
        return (
            <div className="p-4 sm:p-8">
                <Alert title="Keine Daten" message="Es konnten keine Veranstaltungsinformationen gefunden werden."
                       type="info"/>
            </div>
        );
    }

    return (
        <div className={`shell text-gray-800 flex flex-col safe-area-padding ${view.name === 'chat' ? 'h-dvh overflow-hidden' : 'min-h-screen'}`}>
            {/* Global offline indicator: only visible when online === false. Small centered badge below the header so it doesn't block the view. */}
            {typeof online !== 'undefined' && !online && (
                <div
                    className="fixed z-50 pointer-events-none"
                    style={{top: '4rem', left: '50%', transform: 'translateX(-50%)'}}
                >
                    <div
                        className="bg-red-50 border border-red-300 text-red-700 px-2 py-0.5 rounded-full flex items-center gap-2 shadow pointer-events-auto text-xs"
                        role="status"
                        aria-live="polite"
                    >
                        <span className="w-2 h-2 bg-red-600 rounded-full" aria-hidden="true"/>
                        <span className="sr-only">offline</span>
                        <span aria-hidden className="text-xs">offline</span>
                    </div>
                </div>
            )}
            {view.name !== 'home' && (
                <Header
                    title={getHeaderTitle()}
                    showBack={true}
                    onBack={handleBack}
                    showMenu={true}
                    showAddChat={view.name === 'chat' && !view.conversationId}
                    onAddChat={() => setIsCreateChatOpen(true)}
                    onChatParticipantClick={
                        view.name === 'chat' && view.conversationId && eventData?.chat_conversations
                            ? (() => {
                                const conv = eventData.chat_conversations.find(c => c.id === view.conversationId);
                                if (!conv) return undefined;
                                return () => navigateToParticipant(dispatch, conv.otherParticipant.id);
                            })()
                            : undefined
                    }
                    chatParticipant={
                        view.name === 'chat' && view.conversationId && eventData?.chat_conversations
                            ? (() => {
                                const conv = eventData.chat_conversations.find(c => c.id === view.conversationId);
                                return conv ? {
                                    profileImage: conv.otherParticipant.profileImage || undefined,
                                    vorname: conv.otherParticipant.vorname,
                                    nachname: conv.otherParticipant.nachname
                                } : undefined;
                            })()
                            : undefined
                    }
                />
            )}
            <main
                className={`flex-1 flex flex-col container mx-auto px-4 max-w-sm md:max-w-2xl lg:max-w-4xl ${view.name === 'chat' ? 'pt-0 pb-0 overflow-hidden' : 'py-6'
                }`}>
                {chatStartError && (
                    <Alert
                        title="Chat konnte nicht gestartet werden"
                        message={chatStartError}
                        type="warning"
                        onClose={() => setChatStartError(null)}
                    />
                )}
                {renderContent()}
            </main>
            {eventData && (
                <NavigationDrawer
                    sections={eventData.homepage.sections}
                    onNavigate={handleNavigate}
                    lastUpdated={lastUpdated ?? null}
                />
            )}
        </div>
    );
};

export default App;
